'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

/**
 * Website-weiter Visit-Tracker für die Supabase-Tabelle `page_visits`.
 *
 * Schreibt (im Gegensatz zum spezialisierten Tracker in
 * `pre-registration-form.tsx`) einen Eintrag für JEDE öffentliche Seite,
 * damit "Unique Visitors" für die gesamte Domain (medizzconnect.com/*)
 * im täglichen Admin-Report berechnet werden können.
 *
 * Ausgenommen sind:
 * - /vorregistrierung  → wird bereits vom PreRegistrationForm mit
 *                        detaillierterem Tracking (Verweildauer, UTM, …) erfasst
 * - /admin, /dashboard → interne, authentifizierte Bereiche (kein Website-Traffic)
 */

const VISITOR_ID_KEY = 'mdz_pre_reg_visitor_id';

function getOrCreateVisitorId(): string {
  if (typeof window === 'undefined') return '';
  try {
    const existing = window.localStorage.getItem(VISITOR_ID_KEY);
    if (existing) return existing;

    const generated = typeof window.crypto?.randomUUID === 'function'
      ? window.crypto.randomUUID()
      : `visitor-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    window.localStorage.setItem(VISITOR_ID_KEY, generated);
    return generated;
  } catch {
    return '';
  }
}

function isExcludedPath(pathname: string): boolean {
  return (
    pathname.includes('/vorregistrierung')
    || pathname.includes('/admin')
    || pathname.includes('/dashboard')
  );
}

interface SiteVisitTrackerProps {
  locale?: string;
}

export default function SiteVisitTracker({ locale }: SiteVisitTrackerProps) {
  const pathname = usePathname();
  const visitStartRef = useRef<number>(0);
  const sentExitRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !pathname || isExcludedPath(pathname)) {
      return undefined;
    }

    visitStartRef.current = Date.now();
    sentExitRef.current = false;

    const visitorId = getOrCreateVisitorId();
    const browserLanguage = navigator.language || '';
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const urlParams = new URLSearchParams(window.location.search);
    const utm = {
      utm_source: urlParams.get('utm_source') || '',
      utm_medium: urlParams.get('utm_medium') || '',
      utm_campaign: urlParams.get('utm_campaign') || '',
      utm_content: urlParams.get('utm_content') || '',
      utm_term: urlParams.get('utm_term') || '',
      referrer: document.referrer || '',
    };

    const basePayload = {
      page_path: pathname,
      visitor_id: visitorId,
      language: locale || '',
      browser_language: browserLanguage,
      timezone,
      ...utm,
    };
    let viewSent = false;
    let idleCallbackId: number | undefined;
    let fallbackTimerId: ReturnType<typeof setTimeout> | undefined;
    const idleWindow = window as unknown as {
      requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
      cancelIdleCallback?: (handle: number) => void;
    };

    const sendPayload = (payload: Record<string, unknown>, keepalive = false) => {
      const body = JSON.stringify(payload);
      try {
        if (keepalive && navigator.sendBeacon) {
          const blob = new Blob([body], { type: 'application/json' });
          if (navigator.sendBeacon('/api/track-visit', blob)) return;
        }
      } catch {
        // Fetch fallback below
      }

      fetch('/api/track-visit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive,
      }).catch(() => {});
    };

    const sendViewEvent = (keepalive = false) => {
      if (viewSent) return;
      viewSent = true;
      sendPayload({ ...basePayload, event_type: 'view' }, keepalive);
    };

    const scheduleViewEvent = () => {
      if (idleWindow.requestIdleCallback) {
        idleCallbackId = idleWindow.requestIdleCallback(() => sendViewEvent(), { timeout: 2000 });
      } else {
        fallbackTimerId = setTimeout(() => sendViewEvent(), 0);
      }
    };

    const cancelScheduledView = () => {
      if (idleCallbackId !== undefined && idleWindow.cancelIdleCallback) {
        idleWindow.cancelIdleCallback(idleCallbackId);
      }
      if (fallbackTimerId !== undefined) clearTimeout(fallbackTimerId);
    };

    if (document.readyState === 'complete') scheduleViewEvent();
    else window.addEventListener('load', scheduleViewEvent, { once: true });

    const sendExitEvent = () => {
      if (sentExitRef.current) return;
      sentExitRef.current = true;
      cancelScheduledView();
      sendViewEvent(true);

      const durationSeconds = Math.max(0, Math.round((Date.now() - visitStartRef.current) / 1000));
      const payload = {
        ...basePayload,
        event_type: 'exit',
        time_on_page_seconds: durationSeconds,
      };
      sendPayload(payload, true);
    };

    window.addEventListener('pagehide', sendExitEvent);

    return () => {
      window.removeEventListener('load', scheduleViewEvent);
      sendExitEvent();
      window.removeEventListener('pagehide', sendExitEvent);
    };
  }, [pathname, locale]);

  return null;
}
